import { useCallback, useMemo, useState } from 'react';
import { isLegal, replay } from '@tentactoe/engine';
import type { GameState, Move } from '@tentactoe/engine';

export interface Game {
  readonly state: GameState;
  readonly play: (move: Move) => void;
  readonly undo: () => void;
  readonly reset: () => void;
  readonly canUndo: boolean;
}

/**
 * Holds a game as its move list and derives the board from it.
 *
 * Keeping moves as the source of truth — rather than a mutable board — is what
 * makes undo a one-liner, and it exercises the same `replay` primitive the
 * server will use to validate moves in Phase 4. Replaying on each change is
 * trivially cheap: a game is at most 81 moves.
 */
export function useGame(): Game {
  const [moves, setMoves] = useState<readonly Move[]>([]);

  const state = useMemo(() => {
    const result = replay(moves);
    if (!result.ok) {
      // Unreachable: moves only ever grows via `play`, which checks legality.
      throw new Error(`corrupt move list: ${result.reason}`);
    }
    return result.state;
  }, [moves]);

  const play = useCallback(
    (move: Move) => {
      if (!isLegal(state, move)) return;
      setMoves((prev) => [...prev, move]);
    },
    [state],
  );

  const undo = useCallback(() => setMoves((prev) => prev.slice(0, -1)), []);
  const reset = useCallback(() => setMoves([]), []);

  return { state, play, undo, reset, canUndo: moves.length > 0 };
}
